import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { Projection } from '@v2/sdk'

/** Players are a row of small chips; detail appears only when a chip is opened. */
export function PlayerStrip({ projection }: { projection: Projection }) {
  const [open, setOpen] = useState<string | null>(null)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const dismiss = (event: PointerEvent) => { if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(null) }
    document.addEventListener('pointerdown', dismiss, true)
    return () => document.removeEventListener('pointerdown', dismiss, true)
  }, [open])
  useEffect(() => { setOpen(null) }, [projection.viewer])
  if (!projection.players.length) return null
  const opened = projection.players.find(player => player.id === open)
  return <div className="tt-players" ref={root} aria-label="Players" role="group">
    <div className="tt-players-row">
      {projection.players.map(player => {
        const active = player.id === projection.activePlayer
        const you = player.id === projection.viewer
        return <button type="button" key={player.id} className={`tt-player${active ? ' is-active' : ''}${you ? ' is-you' : ''}`} style={{ '--player-color': player.color } as CSSProperties}
          aria-expanded={open === player.id} aria-label={`${player.name}${you ? ' (you)' : ''}, ${player.score} points${player.counter ? `, ${player.counter.value} ${player.counter.label}` : ''}${active ? ', playing now' : ''}`} onClick={() => setOpen(current => current === player.id ? null : player.id)}>
          <span className="tt-player-avatar" aria-hidden="true">{player.name.slice(0, 1).toUpperCase()}</span>
          <span className="tt-player-name" aria-hidden="true">{player.name}</span>
          <span className="tt-player-score" aria-hidden="true">{player.score}</span>
          {player.counter && <span className={`tt-player-counter${player.counter.warning ? ' is-warning' : ''}`} aria-hidden="true"><b>{player.counter.value}</b> {player.counter.label}</span>}
        </button>
      })}
    </div>
    {opened && <div className="tt-player-detail" role="dialog" aria-label={`${opened.name} details`}>
      <strong>{opened.name}{opened.id === projection.viewer && <span> · you</span>}</strong>
      <p>{opened.detail || (opened.id === projection.activePlayer ? 'Playing now.' : 'At the table.')}</p>
      <span className="tt-player-detail-score">{opened.score} points</span>
    </div>}
  </div>
}
